import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths } from 'src/paths';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import patientListIcon from '../../icons/untitled-ui/duocolor/patientListIcon.svg';
import planOfCareIcon from '../../icons/untitled-ui/duocolor/planOfCareIcon.svg';
import { ArrowBackOutlined } from '@mui/icons-material';
import moment from 'moment';
import { useEffect } from 'react';
import DeletePopupModal from 'src/components/common-modals/delete-modal';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import {
  createAssessment,
  deleteSingleMdsAssessment,
  getCaseMixGroup,
  getPatientDateOfService,
  getPlanOfCare,
  resetCreateAssessment,
  resetDeleteSingleMdsAssessment,
  resetUpdateAssessment,
  updateAssessment,
  verifyMdsPps,
  resetVerifyMdsPps,
  getFiveDayArdWindowAction,
  resetGetFiveDayArdWindowAction,
} from 'src/redux/actions/therapyPlanning';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import { TrashIcon } from '../therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import FinalBasicDetailsMdsAssessment from './final-basic-details-mds-assessment';
import {
  assessmentTypeList,
  getAssessmentTypeLabel,
  getAssessmentTypeDbValue,
  getNursingDisplayValue,
} from 'src/utils/mdsConstants';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import PdpmEstimatePanel from 'src/sections/dashboard/pdpm-estimator/pdpm-estimate-panel';
import {
  getArdBounds,
  getFiveDayArdError,
  isArdDateDisabled,
  isFiveDayArdWindowError,
  isInsideFiveDayWindow,
} from 'src/utils/fiveDayArdWindow';

const {
  therapyPlanning,
  patients,
  patientDetails: patientDetailsPath,
  mdsAssessments,
  createMdsAssessment,
} = paths;

const ntaCaseMixGroup = ['NA', 'NB', 'NC', 'ND', 'NE', 'NF'];

const extractNursingCode = (value: string): string => {
  if (!value || value === 'Not Applicable') return value;
  if (value.includes('-')) {
    return value.split('-')[0].trim();
  }
  return value.trim();
};

export const caseMixGroup = [
  'PA1',
  'PA2',
  'PBC1',
  'PBC2',
  'PDE1',
  'PDE2',
  'BAB1',
  'BAB2',
  'CA1',
  'CA2',
  'CBC1',
  'CBC2',
  'CDE1',
  'CDE2',
  'LBC1',
  'LBC2',
  'LDE1',
  'LDE2',
  'HBC1',
  'HBC2',
  'HDE1',
  'HDE2',
  'ES1',
  'ES2',
  'ES3',
];

const ptOtLists = [
  'TA - 60 mins',
  'TB - 90 mins',
  'TC - 120 mins',
  'TD - 160 mins',
  'TE - 190 mins',
  'TF - 120 mins',
  'TG - 120 mins',
  'TH - 120 mins',
  'TI - 120 mins',
  'TJ - 40 mins',
  'TK - 50 mins',
  'TL - 60 mins',
  'TM - 10 mins',
  'TN - 10 mins',
  'TO - 60 mins',
  'TP - 40 mins',
];

const slpLists = [
  'SA - 90 mins',
  'SB - 120 mins',
  'SC - 160 mins',
  'SD - 190 mins',
  'SE - 120 mins',
  'SF - 120 mins',
  'SG - 120 mins',
  'SH - 120 mins',
  'SI - 120 mins',
  'SJ - 120 mins',
  'SK - 120 mins',
  'SL - 120 mins',
];

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: '250px',
      width: 300,
    },
  },
};

function getCurrentDateUTCWithoutTime(date: any) {
  // Get the date components
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string with time portion set to 00:00:00.000Z
  const utcDateString = `${utcDateFormate}T00:00:00.000Z`;

  return utcDateString;
}

const TherapyPlanningEditDetails = () => {
  const [open, setOpen] = useState(false);
  const { state, pathname } = useLocation();
  const facilitiesId = localStorage.getItem('facilityId');
  const [ptOtList, setPtOtList] = useState<any>([]);
  const [slpList, setSlpList] = useState<any>([]);
  const facilityCaseMixResponse = useSelector(
    (state: any) => state?.getCaseMixGroupReducer?.response?.data
  );
  const getMultipleMdsAssessment = useSelector((state: any) => state.getMultipleMDSAssessment);

  const [error, setError] = useState('');
  // THIN-8151: a server rejection for the 5-Day window rule, shown under the picker as well as in the
  // snackbar. No "Save Anyway" -- this is a hard block.
  const [serverArdError, setServerArdError] = useState('');
  const action = state?.action;
  const dispatch = useDispatch();
  const selectedPatient = state?.selectedPatient;
  const assessmentDetails = state?.assessmentDetails;
  const assessmentDetailsEditResponse = useSelector((state: any) => state?.updateAssessmentReducer);
  const assessmentDetailsCreateResponse = useSelector(
    (state: any) => state?.createAssessmentReducer
  );
  const isFromPatientsDetails =
    pathname === `${patients}/${patientDetailsPath}${mdsAssessments}${createMdsAssessment}`;

  // Get the first EOC start date from all episodes
  const getFirstEOCStartDate = () => {
    const episodes = getMultipleMdsAssessment?.response?.data || [];
    if (episodes.length === 0) {
      return null;
    }

    // Sort episodes by startDate to find the earliest one
    const sortedEpisodes = [...episodes].sort((a: any, b: any) => {
      const dateA = a?.startDate ? moment(a.startDate) : moment(0);
      const dateB = b?.startDate ? moment(b.startDate) : moment(0);
      return dateA.valueOf() - dateB.valueOf();
    });

    return sortedEpisodes[0]?.startDate || null;
  };

  const firstEOCStartDate = getFirstEOCStartDate();

  // THIN-8151 / THIN-8287: MDS days 1-8 of each Part A payer period, resolved by the server. Only a 5-Day
  // (PPS) reads them; every other type keeps the EOC-anchored picker. Derived per render, so switching the
  // type dropdown re-boxes or frees the picker at once.
  const fiveDayArdWindows = useSelector(
    (state: any) => state?.getFiveDayArdWindowReducer?.response?.data
  );
  const boundsFor = (assessmentTypeLabel: string) =>
    getArdBounds(
      getAssessmentTypeDbValue(assessmentTypeLabel) === '5 Day',
      fiveDayArdWindows,
      firstEOCStartDate
    );

  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );
  const navigate = useNavigate();
  useEffect(() => {
    if (selectedPatient && open) {
      dispatch(getPlanOfCare({ patientId: selectedPatient?.uuid, episodeOfCareUuid: '' }));
    }
  }, [selectedPatient, open]);

  const handleNavigation = () => {
    if (isFromPatientsDetails) {
      navigate(`${patients}/${patientDetailsPath}${mdsAssessments}`, {
        state: {
          patientId: selectedPatient?.uuid,
        },
      });
    } else if (state?.from === 'ARD_Tab') {
      navigate(`${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.info}`, {
        state: {
          patientId: selectedPatient?.uuid,
          flag: true,
          from_mds: 'ARD_Tab',
        },
      });
    } else {
      navigate('/scheduling/therapy-planning/details', {
        state: {
          patientId: selectedPatient?.uuid,
          flag: true,
        },
      });
    }
  };

  useEffect(() => {
    if (
      assessmentDetailsEditResponse?.isAssessmentUpdated === false &&
      assessmentDetailsEditResponse?.response === null
    )
      return;
    else if (
      assessmentDetailsEditResponse?.isAssessmentUpdated === false &&
      assessmentDetailsEditResponse?.response !== null
    ) {
      setError(
        assessmentDetailsEditResponse?.response?.message ||
          assessmentDetailsEditResponse?.response?.title
      );
      if (isFiveDayArdWindowError(assessmentDetailsEditResponse?.response?.message)) {
        setServerArdError(assessmentDetailsEditResponse.response.message);
      }
      setTimeout(() => {
        dispatch(resetUpdateAssessment());
      }, 3000);
    } else {
      setError('MDS assessment updated successfully.');
      setTimeout(() => {
        dispatch(resetUpdateAssessment());
        handleNavigation();
      }, 3000);
    }
  }, [assessmentDetailsEditResponse]);

  useEffect(() => {
    if (
      assessmentDetailsCreateResponse?.isAssessmentCreated === false &&
      assessmentDetailsCreateResponse?.response === null
    )
      return;
    else if (
      assessmentDetailsCreateResponse?.isAssessmentCreated === false &&
      assessmentDetailsCreateResponse?.response !== null
    ) {
      setError(
        assessmentDetailsCreateResponse?.response?.message ||
          assessmentDetailsCreateResponse?.response?.title
      );
      if (isFiveDayArdWindowError(assessmentDetailsCreateResponse?.response?.message)) {
        setServerArdError(assessmentDetailsCreateResponse.response.message);
      }
      setTimeout(() => {
        dispatch(resetCreateAssessment());
      }, 3000);
    } else {
      setError('MDS assessment created successfully.');
      dispatch(
        getPatientDateOfService({
          patientId: selectedPatient?.uuid,
        })
      );
      setTimeout(() => {
        dispatch(resetCreateAssessment());
        handleNavigation();
      }, 3000);
    }
  }, [assessmentDetailsCreateResponse]);

  const [isFinalCaseMixGroup, setFinalCaseMixGroup] = useState(false);

  useEffect(() => {
    if (action === 'Edit' && assessmentDetails?.isFinalMds) {
      setFinalCaseMixGroup(assessmentDetails?.isFinalMds);
    }
  }, [assessmentDetails]);
  const assessmentDateWithoutDateFormat = assessmentDetails?.assessmentDate?.split('T')?.at(0);
  const assesssmentDateWithoutTimezone = new Date(
    new Date(assessmentDateWithoutDateFormat).valueOf() +
      new Date(assessmentDateWithoutDateFormat).getTimezoneOffset() * 60 * 1000
  );

  const assessmentDateFinalDateWithoutTimezone = assessmentDetails?.finalMds?.assessmentDate
    ?.split('T')
    ?.at(0);

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      assessmentDate:
        state?.from === 'ARD_Tab' && action === 'Create'
          ? new Date(
              new Date(state?.ARD_Date)?.valueOf() +
                new Date(state?.ARD_Date)?.getTimezoneOffset() * 60 * 1000
            )
          : action === 'Edit'
          ? assessmentDetails?.assessmentDate
            ? assesssmentDateWithoutTimezone
            : null
          : null,
      assessmentType:
        action === 'Edit' ? getAssessmentTypeLabel(assessmentDetails?.assessmentType) : '',
      ptOt: action === 'Edit' ? assessmentDetails?.mdsPtOtGroup : '',
      slp: action === 'Edit' ? assessmentDetails?.mdsSlpGroup : '',
      nursing:
        action === 'Edit' && assessmentDetails?.mdsNursingGroup
          ? getNursingDisplayValue(assessmentDetails?.mdsNursingGroup)
          : '',
      nta: action === 'Edit' ? assessmentDetails?.mdsNtaGroup : '',
      isFinalMds: action === 'Edit' ? assessmentDetails?.isFinalMds : false,
      finalMds: {
        assessmentDate:
          action === 'Edit'
            ? assessmentDetails?.finalMds?.assessmentDate
              ? new Date(
                  new Date(assessmentDateFinalDateWithoutTimezone).valueOf() +
                    new Date(assessmentDateFinalDateWithoutTimezone).getTimezoneOffset() * 60 * 1000
                )
              : null
            : null,
        assessmentType:
          action === 'Edit'
            ? getAssessmentTypeLabel(assessmentDetails?.finalMds?.assessmentType)
            : '',
        mdsPtOtGroup: action === 'Edit' ? assessmentDetails?.finalMds?.mdsPtOtGroup : '',
        mdsNursingGroup:
          action === 'Edit' && assessmentDetails?.finalMds?.mdsNursingGroup
            ? getNursingDisplayValue(assessmentDetails.finalMds.mdsNursingGroup)
            : '',
        mdsSlpGroup: action === 'Edit' ? assessmentDetails?.finalMds?.mdsSlpGroup : '',
        mdsNtaGroup: action === 'Edit' ? assessmentDetails?.finalMds?.mdsNtaGroup : '',
      },
    },
    validationSchema: Yup.object({
      assessmentDate: Yup.date()
        .required('Please enter valid Assessment Date')
        .typeError('Please enter valid Assessment Date')
        .test(
          'isAfterFirstEOC',
          "Assessment Reference Date must be on or after the patient's first EOC start date",
          function (value) {
            // Inside a Part A payer the 5-Day window is the edge: its payer start can precede the episode.
            if (!value || !firstEOCStartDate) return true;
            if (isInsideFiveDayWindow(value, boundsFor(this.parent.assessmentType))) return true;
            const assessmentDate = moment(value);
            const firstEOCDate = moment(firstEOCStartDate);
            return assessmentDate.isSameOrAfter(firstEOCDate, 'day');
          }
        )
        .test('fiveDayArdWindow', '', function (value) {
          const message = getFiveDayArdError(value, boundsFor(this.parent.assessmentType));
          return message ? this.createError({ message }) : true;
        }),
      assessmentType: Yup.string().max(255).required('Please enter valid Assessment Type'),
      ptOt: Yup.string(),
      slp: Yup.string(),
      nursing: Yup.string(),
      nta: Yup.string(),
      isFinalMds: Yup.boolean(),

      finalMds: Yup.object().when(['isFinalMds'], (isFinalMds, schema) => {
        return isFinalMds[0]
          ? schema.shape({
              assessmentDate: Yup.date()
                .required('Please enter valid Assessment Date')
                .typeError('Please enter valid Assessment Date')
                .test(
                  'isAfterFirstEOC',
                  "Assessment Reference Date must be on or after the patient's first EOC start date",
                  function (value) {
                    if (!value || !firstEOCStartDate) return true;
                    if (isInsideFiveDayWindow(value, boundsFor(this.parent.assessmentType)))
                      return true;
                    const assessmentDate = moment(value);
                    const firstEOCDate = moment(firstEOCStartDate);
                    return assessmentDate.isSameOrAfter(firstEOCDate, 'day');
                  }
                )
                .test('fiveDayArdWindow', '', function (value) {
                  const message = getFiveDayArdError(value, boundsFor(this.parent.assessmentType));
                  return message ? this.createError({ message }) : true;
                }),
              assessmentType: Yup.string().max(255).required('Please enter valid Assessment Type'),
              mdsPtOtGroup: Yup.string().required('Please enter valid PT-OT'),
              mdsNursingGroup: Yup.string().required('Please enter valid Nursing'),
              mdsSlpGroup: Yup.string().required('Please enter valid SLP'),
              mdsNtaGroup: Yup.string().required('Please enter valid NTA'),
            })
          : schema.shape({
              assessmentDate: Yup.mixed().nullable(),
              assessmentType: Yup.mixed(),
              mdsPtOtGroup: Yup.mixed(),
              mdsNursingGroup: Yup.mixed(),
              mdsSlpGroup: Yup.mixed(),
              mdsNtaGroup: Yup.mixed(),
            });
      }),
    }),

    onSubmit: async (values): Promise<void> => {
      // Call backend verify-pps before saving
      setPendingFormValues(values);
      dispatch(
        verifyMdsPps({
          patientId: selectedPatient?.uuid,
          data: {
            assessmentType: getAssessmentTypeDbValue(values?.assessmentType),
            assessmentDate: getCurrentDateUTCWithoutTime(values?.assessmentDate || new Date()),
            isFinalMds: values?.isFinalMds,
            finalMds: values?.isFinalMds
              ? {
                  assessmentDate: getCurrentDateUTCWithoutTime(
                    values?.finalMds?.assessmentDate || new Date()
                  ),
                  assessmentType: getAssessmentTypeDbValue(values?.finalMds.assessmentType),
                }
              : null,
          },
        })
      );
    },
  });

  // THIN-8151 / THIN-8287: one fetch per patient for every Part A payer period, so the picker can box each
  // stay's own MDS days 1-8 rather than the period covering one anchor date.
  useEffect(() => {
    if (!selectedPatient?.uuid) return;
    dispatch(getFiveDayArdWindowAction({ patientId: selectedPatient.uuid }));
    return () => {
      dispatch(resetGetFiveDayArdWindowAction());
    };
  }, [selectedPatient?.uuid]);

  const ardBounds = boundsFor(formik.values.assessmentType);
  const finalArdBounds = boundsFor(formik.values.finalMds?.assessmentType);

  // The server's rejection clears as soon as the date or type changes.
  useEffect(() => {
    setServerArdError('');
  }, [
    formik.values.assessmentDate,
    formik.values.assessmentType,
    formik.values.finalMds?.assessmentDate,
  ]);

  // The 5-Day window message shows as soon as it applies; other date errors wait for a touch, as before.
  const ardError =
    (formik.errors.assessmentDate &&
    (formik.touched.assessmentDate || isFiveDayArdWindowError(formik.errors.assessmentDate))
      ? formik.errors.assessmentDate
      : '') || serverArdError;
  const fiveDayArdBlocked =
    isFiveDayArdWindowError(formik.errors.assessmentDate) ||
    isFiveDayArdWindowError(formik.errors.finalMds?.assessmentDate) ||
    !!serverArdError;

  useEffect(() => {
    if (selectedPatient?.isNonTherapy) {
      if (formik.values.ptOt !== 'Not Applicable') {
        formik.setFieldValue('ptOt', 'Not Applicable');
      }
      if (formik.values.slp !== 'Not Applicable') {
        formik.setFieldValue('slp', 'Not Applicable');
      }
      if (formik.values.finalMds?.mdsPtOtGroup !== 'Not Applicable') {
        formik.setFieldValue('finalMds.mdsPtOtGroup', 'Not Applicable');
      }
      if (formik.values.finalMds?.mdsSlpGroup !== 'Not Applicable') {
        formik.setFieldValue('finalMds.mdsSlpGroup', 'Not Applicable');
      }
    }
  }, [
    selectedPatient?.isNonTherapy,
    formik.values.ptOt,
    formik.values.slp,
    formik.values.finalMds?.mdsPtOtGroup,
    formik.values.finalMds?.mdsSlpGroup,
    formik,
  ]);

  /** Dispatches the actual create/update assessment action */
  const dispatchSave = (values: any) => {
    if (action === 'Create')
      dispatch(
        createAssessment({
          patientId: selectedPatient?.uuid,
          moduleType: 'PDPM',
          data: {
            assessmentDate: getCurrentDateUTCWithoutTime(values?.assessmentDate || new Date()),
            assessmentType: getAssessmentTypeDbValue(values?.assessmentType),
            mdsPtOtGroup: values?.ptOt,
            mdsNursingGroup: extractNursingCode(values?.nursing).trim(),
            mdsSlpGroup: values?.slp,
            mdsNtaGroup: values?.nta,
            isFinalMds: values?.isFinalMds,
            finalMds: values?.isFinalMds
              ? {
                  assessmentDate: getCurrentDateUTCWithoutTime(
                    values?.finalMds?.assessmentDate || new Date()
                  ),
                  assessmentType: getAssessmentTypeDbValue(values?.finalMds.assessmentType),
                  mdsPtOtGroup: values?.finalMds?.mdsPtOtGroup,
                  mdsNursingGroup: extractNursingCode(values?.finalMds?.mdsNursingGroup).trim(),
                  mdsSlpGroup: values?.finalMds?.mdsSlpGroup,
                  mdsNtaGroup: values?.finalMds?.mdsNtaGroup,
                }
              : null,
          },
        })
      );
    else
      dispatch(
        updateAssessment({
          patientId: selectedPatient?.uuid,
          moduleType: 'PDPM',
          data: {
            uuid: assessmentDetails?.uuid,
            assessmentDate: getCurrentDateUTCWithoutTime(values?.assessmentDate || new Date()),
            assessmentType: getAssessmentTypeDbValue(values?.assessmentType),
            mdsPtOtGroup: values?.ptOt,
            mdsNursingGroup: extractNursingCode(values?.nursing).trim(),
            mdsSlpGroup: values?.slp,
            mdsNtaGroup: values?.nta,
            isFinalMds: values?.isFinalMds,
            finalMds: values?.isFinalMds
              ? {
                  assessmentDate: getCurrentDateUTCWithoutTime(
                    values?.finalMds?.assessmentDate || new Date()
                  ),
                  assessmentType: getAssessmentTypeDbValue(values?.finalMds.assessmentType),
                  mdsPtOtGroup: values?.finalMds?.mdsPtOtGroup,
                  mdsNursingGroup: extractNursingCode(values?.finalMds?.mdsNursingGroup).trim(),
                  mdsSlpGroup: values?.finalMds?.mdsSlpGroup,
                  mdsNtaGroup: values?.finalMds?.mdsNtaGroup,
                }
              : null,
          },
        })
      );
  };

  // Auto-sync finalMds.assessmentType with assessmentType when finalize is enabled
  useEffect(() => {
    if (isFinalCaseMixGroup && formik.values.assessmentType) {
      formik.setFieldValue('finalMds.assessmentType', formik.values.assessmentType);
    }
  }, [formik.values.assessmentType, isFinalCaseMixGroup]);

  // BRK-1967: the PDPM estimate panel takes the ARD as a stable 'YYYY-MM-DD'
  // string — passing a Date would hand its fetch effect a new object every
  // render and loop. Null until a valid date is picked, which is the panel's
  // "no ARD" state.
  const pdpmArd =
    formik.values.assessmentDate && moment(formik.values.assessmentDate).isValid()
      ? moment(formik.values.assessmentDate).format('YYYY-MM-DD')
      : null;

  // THIN-7933: the CMG picker options come from the case-mix template of the payer in effect
  // on the ARD. Keyed to the MAIN assessment date only — the option lists are shared with the
  // Finalize section's pickers, so keying the fetch to the final ARD would swap the options
  // underneath the anticipated pickers whenever the final date is edited.
  useEffect(() => {
    if (state?.selectedPatient?.uuid && pdpmArd) {
      dispatch(getCaseMixGroup({ patientId: state.selectedPatient.uuid, date: pdpmArd }));
    }
  }, [state?.selectedPatient?.uuid, pdpmArd]);

  useEffect(() => {
    // No ARD picked yet, or no template covers the payer on that ARD → the pickers stay empty.
    // The option labels double as the stored values ("TA - 60 mins"), so without a resolved
    // template there are no valid choices to offer.
    if (!pdpmArd || !facilityCaseMixResponse) {
      setPtOtList([]);
      setSlpList([]);
      return;
    }
    // Collapse an equal range like "500-500" to a single value ("500").
    const formatMins = (mins: any) => {
      if (!mins) return '0';
      const [min, max] = String(mins).split('-');
      return min === max ? min : mins;
    };

    const ptUpdateList = ptOtLists?.map((pt) => {
      const key = pt?.slice(0, 2);
      // Each CMG code maps to its lowercase key in the response (e.g. TO -> "to").
      const mins = facilityCaseMixResponse[key?.toLocaleLowerCase()];
      return `${key} - ${formatMins(mins)} mins`;
    });
    setPtOtList(ptUpdateList || []);

    const slpUpdateList = slpLists?.map((slp) => {
      const key = slp?.slice(0, 2);
      const mins = facilityCaseMixResponse[key?.toLocaleLowerCase()];
      return `${key} - ${formatMins(mins)} mins`;
    });
    setSlpList(slpUpdateList || []);
  }, [facilityCaseMixResponse, facilitiesId, pdpmArd]);

  // "Last, First" for the inputs drawer subline — same shape the page header
  // above uses, and null rather than "undefined undefined" when either is absent
  const pdpmPatientName =
    [selectedPatient?.lastName, selectedPatient?.firstName].filter(Boolean).join(', ') || null;

  // apply-mode writes (BRK-1970): the estimate fills the existing dropdown with
  // the verbatim option label, byte-identical to a manual pick, and the user
  // still saves through the normal flow. `finalMds.mdsPtOtGroup`/`mdsSlpGroup`
  // are intentionally untouched — the Finalize switch copies anticipated → final
  // at toggle time, so once Finalize is already on the final values stay a
  // deliberate manual step (see QA notes in docs/pdpm-estimator-qa-matrix.md).
  const applyEstimateToField = (
    field: 'ptOt' | 'slp',
    optionLabel: string,
    confirmation: string
  ) => {
    formik.setFieldValue(field, optionLabel);
    formik.setFieldTouched(field, true);
    // same transient snackbar the rest of this form uses
    setMessage(confirmation);
    setIsSnackBarOpen(true);
    setTimeout(() => {
      setIsSnackBarOpen(false);
      setMessage('');
    }, 3000);
  };

  const applyPtOtEstimate = (optionLabel: string) =>
    applyEstimateToField('ptOt', optionLabel, 'PT & OT CMG set from estimate');

  const applySlpEstimate = (optionLabel: string) =>
    applyEstimateToField('slp', optionLabel, 'SLP CMG set from estimate');

  const onClose = () => {
    setOpen(false);
  };

  const checkValidation = () => {
    const onlyDate = moment(formik.values.assessmentDate).format('YYYY-MM-DD');
    const openEOCAssessment = getMultipleMdsAssessment?.response?.data?.find(
      (mds: any) => mds.status === 'OPENED'
    )?.mdsAssessments;

    // Convert UI label to DB value for comparison
    const currentAssessmentTypeDbValue = getAssessmentTypeDbValue(formik.values.assessmentType);

    if (action === 'Edit') {
      const filteredList = openEOCAssessment?.filter(
        (mds: any) => mds.uuid !== assessmentDetails.uuid
      );
      return (
        filteredList?.some((mds: any) => {
          if (mds?.isFinalMds) {
            return (
              mds?.finalMds?.assessmentDate?.split('T')[0] === onlyDate &&
              mds?.finalMds?.assessmentType === currentAssessmentTypeDbValue
            );
          }
          return (
            mds?.assessmentDate?.split('T')[0] === onlyDate &&
            mds?.assessmentType === currentAssessmentTypeDbValue
          );
        }) ?? false
      );
    }

    return (
      openEOCAssessment?.some((mds: any) => {
        if (mds?.isFinalMds) {
          return (
            mds?.finalMds?.assessmentDate?.split('T')[0] === onlyDate &&
            mds?.finalMds?.assessmentType === currentAssessmentTypeDbValue
          );
        }
        return (
          mds?.assessmentDate?.split('T')[0] === onlyDate &&
          mds?.assessmentType === currentAssessmentTypeDbValue
        );
      }) ?? false
    );
  };
  const [deleteMdsAssessment, setDeleteMdsAssessment] = useState(false);

  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [message, setMessage] = useState('');

  // Backend-driven PPS verification warning state
  const [payerWarningOpen, setPayerWarningOpen] = useState(false);
  const [payerWarningMessage, setPayerWarningMessage] = useState('');
  const [pendingFormValues, setPendingFormValues] = useState<any>(null);

  const verifyMdsPpsResponse = useSelector((state: any) => state?.verifyMdsPpsReducer);

  // Handle verify-pps API response
  useEffect(() => {
    if (!verifyMdsPpsResponse?.response) return;

    const data = verifyMdsPpsResponse?.response?.data;
    if (data?.hasWarning) {
      setPayerWarningMessage(data?.warningMessage);
      setPayerWarningOpen(true);
    } else {
      // No warning — proceed with save
      if (pendingFormValues) {
        dispatchSave(pendingFormValues);
        setPendingFormValues(null);
      }
    }
    dispatch(resetVerifyMdsPps());
  }, [verifyMdsPpsResponse?.response]);

  const handleDeleteMds = () => {
    dispatch(
      deleteSingleMdsAssessment({
        mdsId: assessmentDetails?.uuid,
      })
    );
  };

  const handleDeleteMdsAssessment = (mds: any) => {
    setDeleteMdsAssessment(true);
  };

  const deleteMdsAssessmentDate = useSelector(
    (state: any) => state.deleteSingleMdsAssessmentReducer
  );
  useEffect(() => {
    if (
      deleteMdsAssessmentDate?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentDate?.response === null
    )
      return;
    if (
      deleteMdsAssessmentDate?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentDate !== null
    ) {
      setMessage(deleteMdsAssessmentDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(resetDeleteSingleMdsAssessment());
        setMessage('');
      }, 3000);
      return;
    } else {
      dispatch(
        getPatientDateOfService({
          patientId: selectedPatient?.uuid,
        })
      );
      setDeleteMdsAssessment(false);
      setMessage(deleteMdsAssessmentDate?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setMessage('');
        setIsSnackBarOpen(false);
        dispatch(resetDeleteSingleMdsAssessment());
        handleNavigation();
      }, 3000);
    }
  }, [deleteMdsAssessmentDate]);

  return (
    <>
      <Stack sx={{ margin: '36px 74px', gap: '1.5rem' }}>
        <Stack gap="1.5rem">
          <Stack
            flexDirection="row"
            gap=".5rem"
            alignItems="center"
          >
            <Link
              variant="body2"
              sx={{ display: 'flex', gap: 1, alignItems: 'center', cursor: 'pointer' }}
              color="#6C737F"
              onClick={() => handleNavigation()}
            >
              <IconButton sx={{ margin: '0', padding: '0' }}>
                <ArrowBackOutlined fontSize="small" />
              </IconButton>
              <Typography
                sx={{ color: '#6C737F' }}
                variant="subtitle2"
              >
                {state?.from === 'ARD_Tab' ? 'Back to Therapy Planning' : 'Back to MDS Assessments'}
              </Typography>
            </Link>
          </Stack>
        </Stack>
        <Typography
          sx={{ padding: '0 24px' }}
          variant="h4"
        >
          {action === 'Create'
            ? `${!isFromPatientsDetails ? 'Therapy Planning - ' : ''} Create MDS Assessment`
            : `${!isFromPatientsDetails ? 'Therapy Planning - ' : ''} Edit Assessment Details`}
        </Typography>
        <Divider sx={{ border: '1px solid #E5E7EB' }} />
        <Stack
          gap="1rem"
          sx={{ padding: '0 24px' }}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            gap="1rem"
            flexDirection="row"
          >
            <Avatar
              src={`data:image/png;base64,${selectedPatient?.profileImage}`}
              sx={{ textTransform: 'uppercase' }}
            >{`${selectedPatient?.lastName?.at(0) || ''}${
              selectedPatient?.firstName?.at(0) || ''
            }`}</Avatar>
            <Stack>
              <Typography variant="h5">{`${selectedPatient?.lastName} ${selectedPatient?.firstName}`}</Typography>
              <Typography
                sx={{ display: 'flex', gap: '.5rem', marginTop: '5px' }}
                variant="captionText"
              >
                <img
                  src={patientListIcon}
                  alt=""
                />
                {selectedPatient?.planTracking ? selectedPatient?.planTracking : '-'}
              </Typography>
            </Stack>
          </Stack>

          <Button
            sx={{ height: 'max-content', padding: '6px 14px', gap: '.5rem' }}
            variant="outlined"
            onClick={() => setOpen(true)}
          >
            <img
              src={planOfCareIcon}
              alt=""
            />
            Plan of Care
          </Button>
        </Stack>
        <Card
          sx={{
            padding: '24px',
            margin: '0 24px',
            display: 'flex',
            borderRadius: '16px',
            flexDirection: 'column',
            gap: '2rem',
          }}
        >
          <form
            onSubmit={formik.handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
          >
            <Stack gap="1.5rem">
              <Typography variant="customHeading">Initial Assessment</Typography>
              {/* <Typography variant="subtitle1">{' Basic Details'}</Typography> */}
            </Stack>
            <Grid
              spacing={3}
              container
            >
              <Grid
                item
                xs={6}
              >
                <DatePicker
                  onChange={(newDate) => {
                    formik.setFieldValue('assessmentDate', newDate);
                  }}
                  label="Assessment Reference Date"
                  value={formik.values.assessmentDate}
                  format="MMM dd, yyyy"
                  minDate={ardBounds.minDate ? moment(ardBounds.minDate).toDate() : undefined}
                  maxDate={ardBounds.maxDate ? moment(ardBounds.maxDate).toDate() : undefined}
                  shouldDisableDate={(day) => isArdDateDisabled(day, ardBounds)}
                  sx={{
                    width: '100%',
                    border: ardError ? '3px solid #F04438' : '',
                    borderRadius: ardError ? '10px' : '',
                  }}
                />
                {ardError && (
                  <Typography
                    padding={'0px 16px'}
                    color={'#F04438'}
                    variant="caption"
                  >
                    {ardError}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  defaultValue={'COMMERCIAL_INSURANCE'}
                  fullWidth
                  error={!!(formik.touched.assessmentType && formik.errors.assessmentType)}
                  helperText={
                    formik.touched.assessmentType && (formik.errors.assessmentType as any)
                  }
                  name="assessmentType"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.assessmentType}
                  label="Assessment Type"
                  select
                >
                  {assessmentTypeList?.map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
                {checkValidation() && (
                  <Typography
                    padding={'5px 14px'}
                    color={'#F04438'}
                    variant="caption"
                    sx={{ display: 'block' }}
                  >
                    An assessment with this type and date already exists for this patient. Please
                    edit the existing record
                  </Typography>
                )}
              </Grid>
            </Grid>
            {/* BRK-1967: advisory estimate sits between picking the ARD and
                filling the CMG dropdowns — it self-suppresses when the flag is
                off, the EOC is not therapy-driven, or no ARD is set yet */}
            <PdpmEstimatePanel
              patientUuid={selectedPatient?.uuid}
              ard={pdpmArd}
              ptOtOptions={ptOtList}
              slpOptions={slpList}
              onApplyPtOt={applyPtOtEstimate}
              onApplySlp={applySlpEstimate}
              patientName={pdpmPatientName}
            />
            {/* <Typography variant="subtitle1">{'Case-Mix Group - Anticipated'}</Typography> */}
            <Grid
              spacing={3}
              container
            >
              <Grid
                item
                xs={6}
              >
                <TextField
                  defaultValue={'COMMERCIAL_INSURANCE'}
                  fullWidth
                  error={!!(formik.touched.ptOt && formik.errors.ptOt)}
                  helperText={formik.touched.ptOt && (formik.errors.ptOt as any)}
                  name="ptOt"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={selectedPatient?.isNonTherapy ? 'Not Applicable' : formik.values.ptOt}
                  disabled={selectedPatient?.isNonTherapy}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                  label="PT + OT CMG"
                  select
                >
                  <MenuItem
                    key={'Not Applicable'}
                    value={'Not Applicable'}
                  >
                    Not Applicable
                  </MenuItem>
                  {!selectedPatient?.isNonTherapy &&
                    ptOtList?.map((list: any) => (
                      <MenuItem
                        key={list}
                        value={list}
                      >
                        {list}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  defaultValue={'COMMERCIAL_INSURANCE'}
                  fullWidth
                  label="SLP CMG"
                  error={!!(formik.touched.slp && formik.errors.slp)}
                  helperText={formik.touched.slp && (formik.errors.slp as any)}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={selectedPatient?.isNonTherapy ? 'Not Applicable' : formik.values.slp}
                  disabled={selectedPatient?.isNonTherapy}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                  name="slp"
                  select
                >
                  <MenuItem
                    key={'Not Applicable'}
                    value={'Not Applicable'}
                  >
                    Not Applicable
                  </MenuItem>
                  {!selectedPatient?.isNonTherapy &&
                    slpList.map((list: any) => (
                      <MenuItem
                        key={list}
                        value={list}
                      >
                        {list}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  defaultValue={'COMMERCIAL_INSURANCE'}
                  fullWidth
                  label="Nursing CMG"
                  error={!!(formik.touched.nursing && formik.errors.nursing)}
                  helperText={formik.touched.nursing && (formik.errors.nursing as any)}
                  name="nursing"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.nursing}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                  select
                >
                  <MenuItem
                    key={'Not Applicable'}
                    value={'Not Applicable'}
                  >
                    Not Applicable
                  </MenuItem>
                  {caseMixGroup?.map((value) => (
                    <MenuItem
                      key={value}
                      value={getNursingDisplayValue(value)}
                    >
                      {getNursingDisplayValue(value)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  defaultValue={'COMMERCIAL_INSURANCE'}
                  fullWidth
                  label="NTA CMG"
                  name="nta"
                  error={!!(formik.touched.nta && formik.errors.nta)}
                  helperText={formik.touched.nta && (formik.errors.nta as any)}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.nta}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                  select
                >
                  <MenuItem
                    key={'Not Applicable'}
                    value={'Not Applicable'}
                  >
                    Not Applicable
                  </MenuItem>
                  {ntaCaseMixGroup?.map((value) => (
                    <MenuItem
                      key={value}
                      value={value}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid
              display={'flex'}
              justifyContent={'space-between'}
            >
              <Box>
                <Typography
                  sx={{ fontSize: '16px', color: '#111927' }}
                  variant="subtitle1"
                >
                  {/* {action === 'Edit' ? 'Edit Case-mix Group Details' : 'Add Case-mix Group Details'} */}
                  Finalize Assessment
                </Typography>
                <Typography
                  sx={{ color: '#6C737F', fontSize: '14px' }}
                  variant="subtitle1"
                >
                  Finalize the case mix groups for this assessment
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  checked={isFinalCaseMixGroup}
                  onChange={(e) => {
                    formik.setFieldValue('isFinalMds', e.target.checked);
                    formik.setFieldValue('finalMds.assessmentDate', formik.values.assessmentDate);
                    formik.setFieldValue('finalMds.mdsPtOtGroup', formik.values.ptOt);
                    formik.setFieldValue(
                      'finalMds.mdsNursingGroup',
                      formik.values.nursing
                        ? getNursingDisplayValue(extractNursingCode(formik.values.nursing))
                        : ''
                    );
                    formik.setFieldValue('finalMds.mdsSlpGroup', formik.values.slp);
                    formik.setFieldValue('finalMds.mdsNtaGroup', formik.values.nta);
                    formik.setFieldValue('finalMds.assessmentType', formik.values.assessmentType);
                    setFinalCaseMixGroup(e.target.checked);
                  }}
                />
              </Box>
            </Grid>
            {isFinalCaseMixGroup ? (
              <FinalBasicDetailsMdsAssessment
                slpList={slpList}
                formik={formik}
                selectedPatient={selectedPatient}
                caseMixGroup={caseMixGroup}
                ptOtList={ptOtList}
                ardBounds={finalArdBounds}
              />
            ) : null}

            <Stack
              flexDirection="row"
              gap="1rem"
              justifyContent={action === 'Edit' ? 'space-between' : 'flex-end'}
            >
              {action === 'Edit' && (
                <IconButton
                  onClick={handleDeleteMdsAssessment}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '11.765px',
                    background: 'var(--error-light, #FEE4E2)',
                    '&:hover': {
                      background: 'var(--error-light, #FEE4E2)',
                    },
                  }}
                >
                  <TrashIcon />
                </IconButton>
              )}
              <Stack
                flexDirection="row"
                gap="1rem"
              >
                <Button
                  variant="text"
                  sx={{ color: '#111927', fontSize: '14px', fontWeight: '600' }}
                  onClick={handleNavigation}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  disabled={checkValidation() || fiveDayArdBlocked}
                  loading={assessmentDetailsCreateResponse?.loading}
                >
                  {action === 'Create' ? 'Save' : 'Save Details'}
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </Card>
        <Grid />
      </Stack>
      <Drawer
        anchor="right"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 500,
          },
        }}
      >
        <Stack>
          <Stack
            justifyContent="space-between"
            sx={{ margin: '1.5rem' }}
            flexDirection="row"
          >
            <Typography variant="h5">{`${selectedPatient?.name}'s Plan of Care`}</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack
            gap="1rem"
            sx={{ margin: '1.3rem' }}
          >
            <Stack
              sx={{ marginTop: '1rem' }}
              gap=".75rem"
            >
              <Typography
                variant="subtitle2"
                color="primary"
              >
                DIAGNOSIS
              </Typography>
              <Stack
                flexWrap="wrap"
                gap="1rem"
                flexDirection="row"
              >
                {planOfCareDetails?.diagnosis?.map((name: any, index: any) => (
                  <>
                    <Typography
                      key={index}
                      variant="body1"
                    >
                      {name}
                    </Typography>
                    {index != planOfCareDetails?.diagnosis?.length - 1 && (
                      <Typography variant="body1">,</Typography>
                    )}
                  </>
                ))}
              </Stack>
            </Stack>
            <Divider />
            <Stack
              gap="1rem"
              alignItems="center"
              flexDirection="row"
            >
              <Chip
                variant="outlined"
                label={'OT'}
              />
              <CalendarMonthIcon />
              <Typography>
                {planOfCareDetails?.disciplineMap?.OT ? planOfCareDetails?.disciplineMap?.OT : '-'}
              </Typography>
            </Stack>
            <Stack
              gap="1rem"
              alignItems="center"
              flexDirection="row"
            >
              <Chip
                variant="outlined"
                label={'PT'}
              />
              <CalendarMonthIcon />
              <Typography>
                {planOfCareDetails?.disciplineMap?.PT ? planOfCareDetails?.disciplineMap?.PT : '-'}
              </Typography>
            </Stack>
            <Stack
              gap="1rem"
              alignItems="center"
              flexDirection="row"
            >
              <Chip
                variant="outlined"
                label={'SLP'}
              />
              <CalendarMonthIcon />
              <Typography>
                {planOfCareDetails?.disciplineMap?.SLP
                  ? planOfCareDetails?.disciplineMap?.SLP
                  : '-'}
              </Typography>
            </Stack>
            <Divider />
            <Stack
              flexDirection="row"
              gap="1rem"
            >
              <Box
                sx={{
                  width: '35px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e8edff',
                  borderRadius: '12px',
                }}
              >
                <WorkspacePremiumOutlinedIcon />
              </Box>
              <Stack>
                <Typography variant="body2">Goals</Typography>
                {planOfCareDetails?.goals?.map((goal: any) => (
                  <Typography
                    key={goal}
                    variant="body2"
                  >
                    {goal}
                  </Typography>
                ))}
              </Stack>
            </Stack>
            <Divider />
            <Stack
              flexDirection="row"
              gap="1rem"
            >
              <Box
                sx={{
                  width: '35px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e8edff',
                  borderRadius: '12px',
                }}
              >
                <WorkspacePremiumOutlinedIcon />
              </Box>
              <Stack gap=".5rem">
                <Typography variant="body2">Codes</Typography>
                <Stack
                  gap="1rem"
                  flexDirection="row"
                  flexWrap={'wrap'}
                >
                  {planOfCareDetails?.codes?.map((planOfCare: any) => (
                    <Chip
                      variant="outlined"
                      label={`${planOfCare} | ${
                        planOfCareDetails?.cptCodeDescription[planOfCare] || ''
                      } `}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
      {(assessmentDetailsEditResponse?.response !== null ||
        assessmentDetailsCreateResponse?.response !== null) && (
        <SimpleSnackbar
          message={error}
          isError={
            (!assessmentDetailsEditResponse?.isAssessmentUpdated &&
              assessmentDetailsEditResponse?.response !== null) ||
            (!assessmentDetailsCreateResponse?.isAssessmentCreated &&
              assessmentDetailsCreateResponse?.response !== null)
          }
        />
      )}

      <BootstrapDialog
        onClose={() => setDeleteMdsAssessment(false)}
        aria-labelledby="customized-dialog-title"
        open={deleteMdsAssessment}
      >
        <DeletePopupModal
          close={() => setDeleteMdsAssessment(false)}
          message={'This action cannot be undone.'}
          button={{ saveLabel: 'Delete', closeLabel: 'Cancel' }}
          title={'Delete MDS Assessment?'}
          handleSaveBtn={handleDeleteMds}
        />
      </BootstrapDialog>
      {isSnackBarOpen && (
        <SimpleSnackbar
          message={message}
          isWarning={false}
        />
      )}

      {/* ARD payer overlap warning dialog */}
      <WarningModal
        open={payerWarningOpen}
        title="Payer Period Warning"
        message={payerWarningMessage}
        buttonText="Save Anyway"
        cancelBtn="Go Back"
        onClose={() => {
          setPayerWarningOpen(false);
          setPendingFormValues(null);
        }}
        handleSaveBtn={() => {
          setPayerWarningOpen(false);
          if (pendingFormValues) {
            dispatchSave(pendingFormValues);
            setPendingFormValues(null);
          }
        }}
      />
    </>
  );
};

export default TherapyPlanningEditDetails;
